import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { download } from '@/core/http/files';
import type { DocumentVersionRow } from '@/core/models/approval.models';
import {
  TagExceptionStatusId,
  type DocumentVersionOption,
  type IxbrlFactRow,
  type IxbrlInspectorResult,
  type IxbrlJobRow,
  type IxbrlTagExceptionRow,
  type PeriodOption,
  type TaxonomyConceptOption,
  type TaxonomyVersionRow,
} from '@/core/models/phase4.models';
import {
  OUTPUT_PROFILES,
  outputProfileLabel,
  outputProfileOf,
  selectableOutputProfile,
  type OutputProfile,
} from '@/core/outputProfile';
import { notifyBlobError, notifyError, notifySuccess } from '@/core/notify/notify';
import { approvalService } from '@/core/services/approval';
import { phase4Service } from '@/core/services/phase4';
import { Button } from '@/kit/Button';
import { DataTable } from '@/kit/DataTable';
import { FormField, InputField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { Select, SelectOption } from '@/kit/Select';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { formatDateTime, formatNumber, useBusy, useClientGuard, useLatest } from './shared';
import './TaxreturnIxbrl.scss';

type Tab = 'facts' | 'exceptions';

/** The exception queue only ever shows Open exceptions: Angular's filter was never changed by anything. */
const EXCEPTION_STATUS_FILTER: number | null = TagExceptionStatusId.Open;

function dimensionText(fact: IxbrlFactRow): string {
  const entries = Object.keys(fact.dimensions ?? {});
  return entries.length === 0 ? '-' : entries.map((key) => `${key}=${fact.dimensions[key]}`).join(', ');
}

/**
 * AP-013 iXBRL: the fact inspector and the tagging-exception (top-up) review queue. One screen on two URLs:
 * `ixbrl-exceptions` opens on the exceptions tab, anything else on the fact inspector (decided once, when it opens).
 */
export function TaxreturnIxbrl() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const client = useClientGuard();
  const { isBusy, begin, end } = useBusy();

  const [tab, setTabState] = useState<Tab>(() =>
    `${location.pathname}${location.search}`.includes('ixbrl-exceptions') ? 'exceptions' : 'facts',
  );

  const [periods, setPeriods] = useState<PeriodOption[]>([]);
  /** Every accounts version of the period; `documents` below is the chosen kind of accounts' share of it. */
  const [periodDocuments, setPeriodDocuments] = useState<DocumentVersionOption[]>([]);
  /** Full, Abridged or Filleted: each kind has its own versions, and a submission files one kind's final version. */
  // APT341: a link that names the retired Abridged accounts opens on the Full accounts instead.
  const [outputProfile, setOutputProfile] = useState<OutputProfile>(() =>
    selectableOutputProfile(outputProfileOf({ outputProfile: searchParams.get('profile') })),
  );
  const documents = periodDocuments.filter((d) => outputProfileOf(d) === outputProfile);
  const [taxonomies, setTaxonomies] = useState<TaxonomyVersionRow[]>([]);

  const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  /** Empty means "use the version pinned to the document, or the one accepted at the period end". */
  const [selectedTaxonomyId, setSelectedTaxonomyId] = useState<string | null>(null);

  const [inspector, setInspector] = useState<IxbrlInspectorResult | null>(null);
  const [facts, setFacts] = useState<IxbrlFactRow[]>([]);
  const [job, setJob] = useState<IxbrlJobRow | null>(null);
  const [exceptions, setExceptions] = useState<IxbrlTagExceptionRow[]>([]);
  const [concepts, setConcepts] = useState<TaxonomyConceptOption[]>([]);

  const [searchText, setSearchText] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);

  /** The exception the reviewer is topping up. */
  const [selectedException, setSelectedException] = useState<IxbrlTagExceptionRow | null>(null);
  const [resolveConcept, setResolveConcept] = useState<string | null>(null);
  const [resolveNote, setResolveNote] = useState('');

  // Angular read `this.x` when a request came back; this is the same for the callbacks that outlive a render.
  const current = useLatest({ periodId: selectedPeriodId, documentId: selectedDocumentId, searchText, outputProfile });

  // ---- loaders (each needs the period, most also the document) ----

  const loadJobs = (periodId: string | null, documentId: string | null) => {
    if (!periodId || !documentId) {
      return;
    }
    phase4Service.getJobs(periodId, documentId).then(
      (rows) => setJob(rows.length > 0 ? rows[0] : null),
      (error) => notifyError(error),
    );
  };

  const loadFacts = (periodId: string | null, documentId: string | null, search: string) => {
    if (!periodId || !documentId) {
      return;
    }
    phase4Service.getFacts(periodId, documentId, search || null).then(
      (result) => {
        setInspector(result);
        setFacts(result.facts);
        if (result.job) {
          setJob(result.job);
        }
      },
      (error) => notifyError(error),
    );
  };

  const loadExceptions = (periodId: string | null, documentId: string | null) => {
    if (!periodId) {
      return;
    }
    phase4Service.getExceptions(periodId, documentId, EXCEPTION_STATUS_FILTER).then(
      (rows) => setExceptions(rows),
      (error) => notifyError(error),
    );
  };

  const reloadAll = () => {
    const now = current.current;
    loadJobs(now.periodId, now.documentId);
    loadFacts(now.periodId, now.documentId, now.searchText);
    loadExceptions(now.periodId, now.documentId);
  };

  // ---- selection ----

  const onDocumentChange = (periodId: string | null, list: DocumentVersionOption[], documentId: string | null) => {
    setInspector(null);
    setFacts([]);
    setJob(null);
    setExceptions([]);
    setSelectedException(null);

    if (!periodId || !documentId) {
      return;
    }

    const document = list.find((d) => d.documentVersionId === documentId);
    setSelectedTaxonomyId(document ? document.taxonomyVersionId : null);

    loadJobs(periodId, documentId);
    loadFacts(periodId, documentId, current.current.searchText);
    loadExceptions(periodId, documentId);
  };

  const onPeriodChange = (options: PeriodOption[], periodId: string | null) => {
    const period = options.find((p) => p.accountingPeriodId === periodId);
    const all = period ? period.documents : [];
    setPeriodDocuments(all);
    const list = all.filter((d) => outputProfileOf(d) === current.current.outputProfile);
    const documentId = list.length > 0 ? list[0].documentVersionId : null;
    setSelectedDocumentId(documentId);
    onDocumentChange(periodId, list, documentId);
  };

  /** Another kind of accounts: its versions are listed, and the newest is selected. */
  const onOutputProfileChange = (profile: OutputProfile) => {
    setOutputProfile(profile);
    const list = periodDocuments.filter((d) => outputProfileOf(d) === profile);
    const documentId = list.length > 0 ? list[0].documentVersionId : null;
    setSelectedDocumentId(documentId);
    onDocumentChange(selectedPeriodId, list, documentId);
  };

  // ---- opening (ngOnInit) ----

  useEffect(() => {
    if (!client) {
      return;
    }
    phase4Service.getTaxonomyVersions().then(
      (rows) => setTaxonomies(rows),
      (error) => notifyError(error),
    );
    phase4Service.getPeriodOptions(client.id).then(
      (rows) => {
        setPeriods(rows);
        const queryPeriod = searchParams.get('periodId');
        const period = rows.find((p) => p.accountingPeriodId === queryPeriod) ?? rows[0];
        if (period) {
          setSelectedPeriodId(period.accountingPeriodId);
          onPeriodChange(rows, period.accountingPeriodId);
        }
      },
      (error) => notifyError(error),
    );
    // Runs once, when the screen opens (ngOnInit): the query parameter is read at that moment only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  const setTab = (next: Tab) => {
    setTabState(next);
    if (next === 'exceptions') {
      loadExceptions(selectedPeriodId, selectedDocumentId);
    } else {
      loadFacts(selectedPeriodId, selectedDocumentId, searchText);
    }
  };

  // ---- actions ----

  /**
   * Generates a new accounts document version for the selected period: the accounts version the iXBRL and Companies
   * House filing screens then tag and file.
   */
  const generateDocument = () => {
    const periodId = selectedPeriodId;
    if (!periodId) {
      notifyError('Select an accounting period first.');
      return;
    }
    if (!client || !begin()) {
      return;
    }
    approvalService
      .generateDocument(periodId, outputProfile)
      .then((row) => phase4Service.getPeriodOptions(client.id).then((options) => ({ options, row })))
      .then(
        ({ options, row }: { options: PeriodOption[]; row: DocumentVersionRow }) => {
          setPeriods(options);
          setSelectedPeriodId(periodId);
          const list = options.find((p) => p.accountingPeriodId === periodId)?.documents ?? [];
          setPeriodDocuments(list);
          setSelectedDocumentId(row.documentVersionId);
          notifySuccess(
            outputProfile === 'MEMBERS'
              ? `Accounts document generated (v${row.versionNo}).`
              : `${outputProfileLabel(outputProfile)} document generated (v${row.versionNo}).`,
          );
          onDocumentChange(
            periodId,
            list.filter((d) => outputProfileOf(d) === outputProfile),
            row.documentVersionId,
          );
        },
        (error) => notifyError(error),
      )
      .finally(end);
  };

  /** Tags the accounts. Validate-only reports exceptions without storing an artefact. */
  const generate = (validateOnly: boolean) => {
    if (!selectedPeriodId) {
      notifyError('Select an accounting period first.');
      return;
    }
    if (!selectedDocumentId) {
      notifyError('Select an accounts version first.');
      return;
    }
    if (!begin()) {
      return;
    }
    phase4Service
      .generate({
        accountingPeriodId: selectedPeriodId,
        documentVersionId: selectedDocumentId,
        taxonomyVersionId: selectedTaxonomyId,
        validateOnly,
      })
      .then(
        (result) => {
          setJob(result);
          const summary = `${result.factCount} fact(s), ${result.exceptionCount} exception(s)`;
          if (result.isValid) {
            notifySuccess(`iXBRL ${validateOnly ? 'validated' : 'generated'}: ${summary}.`);
          } else {
            notifyError(`iXBRL did not pass validation: ${summary}.`);
          }
          reloadAll();
        },
        (error) => notifyError(error),
      )
      .finally(end);
  };

  const downloadArtefact = (
    request: (
      period: string,
      document: string,
      taxonomy: string | null,
    ) => ReturnType<typeof phase4Service.downloadIxbrl>,
    failureMessage: string,
  ) => {
    if (!selectedPeriodId || !selectedDocumentId || !begin()) {
      return;
    }
    request(selectedPeriodId, selectedDocumentId, selectedTaxonomyId)
      .then(
        (response) => download(response),
        (error) => notifyBlobError(error, failureMessage),
      )
      .finally(end);
  };

  /** The reviewer's copy of the submission: a PDF of every tagged fact, rendered from the artefact that would be filed. */
  const downloadReviewPack = () =>
    downloadArtefact(
      (period, document, taxonomy) => phase4Service.downloadReviewPack(period, document, taxonomy),
      'The review pack could not be produced.',
    );

  /** The inline XBRL (.xhtml) file itself, exactly as it would be filed with Companies House. */
  const downloadIxbrl = () =>
    downloadArtefact(
      (period, document, taxonomy) => phase4Service.downloadIxbrl(period, document, taxonomy),
      'The iXBRL file could not be produced.',
    );

  const pinTaxonomy = () => {
    if (!selectedPeriodId || !selectedDocumentId || !selectedTaxonomyId) {
      notifyError('Select the taxonomy version to pin.');
      return;
    }
    phase4Service.pinTaxonomy(selectedPeriodId, selectedDocumentId, selectedTaxonomyId).then(
      () => {
        notifySuccess('The taxonomy version is pinned to this accounts version.');
        loadJobs(current.current.periodId, current.current.documentId);
      },
      (error) => notifyError(error),
    );
  };

  /** Fires on every keystroke: no debounce, like Angular. */
  const onSearch = (text: string) => {
    const trimmed = (text ?? '').trim();
    setSearchText(trimmed);
    loadFacts(selectedPeriodId, selectedDocumentId, trimmed);
  };

  const selectException = (row: IxbrlTagExceptionRow) => {
    if (row.status !== TagExceptionStatusId.Open) {
      return;
    }

    setSelectedException(row);
    setResolveConcept(null);
    setResolveNote('');

    if (selectedPeriodId && selectedDocumentId) {
      phase4Service.getConcepts(selectedPeriodId, selectedDocumentId).then(
        (rows) => setConcepts(rows),
        (error) => notifyError(error),
      );
    }
  };

  /** 1 tags the figure with a concept; 2 accepts it untagged with a reason. */
  const resolve = (action: number) => {
    if (!selectedException || !selectedPeriodId) {
      return;
    }

    if (action === 1 && !resolveConcept) {
      notifyError('Select the taxonomy concept to tag this figure with.');
      return;
    }

    if (action === 2 && !resolveNote.trim()) {
      notifyError('Enter the reason this figure is accepted without a tag.');
      return;
    }

    phase4Service
      .resolveException(selectedPeriodId, {
        ixbrlTagExceptionId: selectedException.ixbrlTagExceptionId,
        action,
        conceptQName: action === 1 ? resolveConcept : null,
        note: resolveNote.trim() ? resolveNote.trim() : null,
      })
      .then(
        () => {
          notifySuccess(
            action === 1
              ? 'The figure is tagged. Regenerate the iXBRL to include it.'
              : 'The figure is accepted untagged. Regenerate the iXBRL to refresh the package.',
          );
          setSelectedException(null);
          loadExceptions(current.current.periodId, current.current.documentId);
        },
        (error) => notifyError(error),
      );
  };

  return (
    <SideMenuPage menu="task" contentClassName="taxreturn-ixbrl">
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <Select
              className="w-250"
              openOnFocus
              panelClass="myPanelClass"
              clearable={false}
              placeholder="Accounting period"
              value={selectedPeriodId}
              onChange={(value) => {
                const periodId = (value as string | null) ?? null;
                setSelectedPeriodId(periodId);
                onPeriodChange(periods, periodId);
              }}
            >
              {periods.map((option) => (
                <SelectOption key={option.accountingPeriodId} value={option.accountingPeriodId}>
                  {option.label}
                </SelectOption>
              ))}
            </Select>
            <Select<(typeof OUTPUT_PROFILES)[number]>
              className="w-200"
              items={OUTPUT_PROFILES}
              bindLabel="label"
              bindValue="code"
              clearable={false}
              searchable={false}
              value={outputProfile}
              onChange={(value) => onOutputProfileChange((value as OutputProfile | null) ?? 'MEMBERS')}
            />
            <Select
              className="w-250"
              openOnFocus
              panelClass="myPanelClass"
              clearable={false}
              placeholder="Accounts version"
              value={selectedDocumentId}
              onChange={(value) => {
                const documentId = (value as string | null) ?? null;
                setSelectedDocumentId(documentId);
                onDocumentChange(selectedPeriodId, documents, documentId);
              }}
            >
              {documents.map((option) => (
                <SelectOption key={option.documentVersionId} value={option.documentVersionId}>
                  {option.label}
                </SelectOption>
              ))}
            </Select>
            <Button
              className="action-button secondary-button"
              title="Generate a new accounts document version for this period"
              disabled={!selectedPeriodId || isBusy}
              onClick={generateDocument}
            >
              <Icon name="note_add" outlined />
              <span>Generate accounts document</span>
            </Button>
            <Select
              className="w-250"
              openOnFocus
              panelClass="myPanelClass"
              placeholder="Taxonomy version"
              value={selectedTaxonomyId}
              onChange={(value) => setSelectedTaxonomyId((value as string | null) ?? null)}
            >
              {taxonomies.map((option) => (
                <SelectOption key={option.taxonomyVersionId} value={option.taxonomyVersionId}>
                  {`${option.taxonomyName} (${option.tagMappingCount} tags)`}
                </SelectOption>
              ))}
            </Select>
          </div>

          <div className="content-header-right mb-1">
            <Button
              className="action-button primary-button"
              disabled={!selectedDocumentId || isBusy}
              onClick={() => generate(false)}
            >
              <Icon name="sell" />
              <span>Generate iXBRL</span>
            </Button>
            <Button
              className="action-button secondary-button"
              disabled={!selectedDocumentId || isBusy}
              onClick={() => generate(true)}
            >
              <Icon name="fact_check" outlined />
              <span>Validate only</span>
            </Button>
            <Button
              className="action-button secondary-button"
              disabled={!selectedDocumentId || !selectedTaxonomyId}
              onClick={pinTaxonomy}
            >
              <Icon name="push_pin" outlined />
              <span>Pin taxonomy</span>
            </Button>
            <Button
              className="action-button secondary-button"
              disabled={!selectedDocumentId || isBusy}
              onClick={downloadIxbrl}
            >
              <Icon name="code" outlined />
              <span>Download iXBRL</span>
            </Button>
            <Button
              className="action-button secondary-button"
              disabled={!selectedDocumentId || isBusy}
              onClick={downloadReviewPack}
            >
              <Icon name="picture_as_pdf" outlined className="icon-color-pdf" />
              <span>Review pack</span>
            </Button>
          </div>
        </div>

        {/*
          BUG-049: the figures this page tags are the period's latest saved Annual Accounts report's - the same ones the
          report's own "Download as iXBRL" tags - so say where they come from before anyone generates an empty file.
        */}
        {selectedPeriodId && (
          <p className="text-muted font-size-12 mb-1">
            The iXBRL figures come from this period&apos;s latest saved Annual Accounts report (Reports &gt; Annual
            Accounts), the same as the report&apos;s &quot;Download as iXBRL&quot;. Save the report before generating
            the iXBRL.
          </p>
        )}
        {/* #148/#149: say why the version list and the fact inspector are empty, and what to do next. */}
        {selectedPeriodId && !documents.length && (
          <p className="text-muted font-size-12 mb-1">
            {outputProfile === 'MEMBERS'
              ? 'This period has no accounts version yet. Click "Generate accounts document" to create one, then select it and generate the iXBRL.'
              : `This period has no ${outputProfileLabel(outputProfile)} version yet. Click "Generate accounts document" to create one, then select it and generate the iXBRL.`}
          </p>
        )}
        {selectedDocumentId && !facts.length && tab === 'facts' && !searchText && (
          <p className="text-muted font-size-12 mb-1">
            No tagged facts for this version yet. Generate the iXBRL to see and search them here.
          </p>
        )}
        {selectedDocumentId && !facts.length && tab === 'facts' && searchText && (
          <p className="text-muted font-size-12 mb-1">No tagged facts match &quot;{searchText}&quot;.</p>
        )}

        {job && (
          <div className="sub-wrap">
            <div className="sub-acc">
              <div className="sub-ttl">Last run :</div>
              <div className="sub-dtl">
                {formatDateTime(job.requestedAtUtc)} ({job.statusName})
              </div>
            </div>
            <div className="sub-acc">
              <div className="sub-ttl">Taxonomy :</div>
              <div className="sub-dtl">
                {job.taxonomyName || '-'} {job.taxonomyVersion}
              </div>
            </div>
            <div className="sub-acc">
              <div className="sub-ttl">Facts :</div>
              <div className="sub-dtl">{job.factCount}</div>
            </div>
            <div className="sub-acc">
              <div className="sub-ttl">Exceptions :</div>
              <div className={`sub-dtl ${job.exceptionCount > 0 ? 'text-red' : 'text-green'}`}>
                {job.exceptionCount}
              </div>
            </div>
            <div className="sub-acc">
              <div className="sub-ttl">State :</div>
              <div className={`sub-dtl ${job.isStale ? 'text-red' : 'text-green'}`}>
                {job.isStale ? 'Stale - regenerate before filing' : job.isValid ? 'Valid' : 'Not valid'}
              </div>
            </div>
          </div>
        )}

        <div className="d-flex align-items-center gap-5 mb-1">
          <Button className={tab === 'facts' ? 'primary-button' : ''} onClick={() => setTab('facts')}>
            Fact inspector
          </Button>
          <Button className={tab === 'exceptions' ? 'primary-button' : ''} onClick={() => setTab('exceptions')}>
            Tagging exceptions
            {inspector && inspector.openExceptionCount > 0 && (
              <span className="text-red"> ({inspector.openExceptionCount})</span>
            )}
          </Button>
          {tab === 'facts' && (
            <FormField className="search-field" focused={searchFocused}>
              <Icon name="search" />
              <input
                className="mat-mdc-input-element mdc-text-field__input mat-mdc-form-field-input-control"
                placeholder="Search concept or source"
                autoComplete="off"
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                onInput={(event) => onSearch(event.currentTarget.value)}
              />
            </FormField>
          )}
        </div>

        {tab === 'facts' && (
          <div className="mat-table-wrapper">
            <DataTable
              stickyHeader
              rows={facts}
              rowKey={(fact, index) => fact.ixbrlFactIndexId ?? index}
              columns={[
                { id: 'concept', header: 'Concept', cell: (f) => f.conceptQName },
                { id: 'value', header: 'Value', cell: (f) => f.renderedValue },
                {
                  id: 'context',
                  header: 'Context',
                  cell: (f) => `${f.contextRef} (${f.isCurrentPeriod ? 'current' : 'prior'})`,
                },
                {
                  id: 'unit',
                  header: 'Unit / decimals',
                  cell: (f) => `${f.unitRef || '-'} ${f.decimals ?? ''}`,
                },
                {
                  id: 'source',
                  header: 'Source',
                  cell: (f) => (
                    <>
                      {f.sourceCaption || f.sourceCode}{' '}
                      <span className="text-gray">
                        ({f.sourceType} {f.sourceCode})
                      </span>
                    </>
                  ),
                },
                {
                  id: 'taxonomy',
                  header: 'Taxonomy trace',
                  cell: (f) => (
                    <>
                      {`${f.taxonomyName ?? ''} ${f.taxonomyVersion ?? ''} · ${f.taggingMode ?? ''}`}
                      <div className="text-gray">{dimensionText(f)}</div>
                    </>
                  ),
                },
              ]}
            />
          </div>
        )}

        {tab === 'exceptions' && (
          <div>
            <div className="mat-table-wrapper">
              <DataTable
                stickyHeader
                rows={exceptions}
                rowKey={(row) => row.ixbrlTagExceptionId}
                columns={[
                  {
                    id: 'source',
                    header: 'Source',
                    cell: (e) => (
                      <>
                        {e.sourceCaption || e.sourceCode}
                        <div className="text-gray">
                          {e.sourceType} {e.sourceCode}
                        </div>
                      </>
                    ),
                  },
                  {
                    id: 'amount',
                    header: 'Amount',
                    cell: (e) => (e.amount !== null ? formatNumber(e.amount) : '-'),
                  },
                  {
                    id: 'severity',
                    header: 'Severity',
                    cellClassName: (e) => (e.isBlocking ? 'text-red' : undefined),
                    cell: (e) => e.severityName,
                  },
                  { id: 'reason', header: 'Reason', cell: (e) => e.reason },
                  {
                    id: 'status',
                    header: 'Status',
                    cell: (e) => (
                      <>
                        {e.statusName}
                        {e.resolvedConceptQName && <div className="text-gray">{e.resolvedConceptQName}</div>}
                      </>
                    ),
                  },
                  {
                    id: 'action',
                    header: 'Action',
                    cell: (e) =>
                      e.status === 0 && (
                        <a className="link" onClick={() => selectException(e)}>
                          Resolve
                        </a>
                      ),
                  },
                ]}
              />
            </div>

            {selectedException && (
              <div className="note-box mt-2">
                <div className="form-field">
                  <p>
                    Top up: {selectedException.sourceCaption || selectedException.sourceCode}{' '}
                    <span className="text-gray">({selectedException.sourceCode})</span>
                  </p>
                </div>
                <div className="form-field">
                  <p>Taxonomy concept</p>
                  <Select
                    className="w-387"
                    openOnFocus
                    panelClass="myPanelClass"
                    placeholder="Select a concept from the pinned taxonomy"
                    value={resolveConcept}
                    onChange={(value) => setResolveConcept((value as string | null) ?? null)}
                  >
                    {concepts.map((option) => (
                      <SelectOption key={option.conceptQName} value={option.conceptQName}>
                        {option.conceptQName}
                      </SelectOption>
                    ))}
                  </Select>
                </div>
                <div className="form-field">
                  <p>Note</p>
                  <InputField
                    fieldClassName="w-387"
                    maxLength={1000}
                    name="resolveNote"
                    value={resolveNote}
                    onChange={(event) => setResolveNote(event.target.value)}
                  />
                </div>
                <div className="d-flex align-items-center gap-5">
                  <Button className="action-button primary-button" onClick={() => resolve(1)}>
                    <span>Tag this figure</span>
                  </Button>
                  <Button className="action-button secondary-button" onClick={() => resolve(2)}>
                    <span>Accept untagged</span>
                  </Button>
                  <Button onClick={() => setSelectedException(null)}>
                    <span>Cancel</span>
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </SideMenuPage>
  );
}
